import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, ArrowRight, ShieldCheck } from "lucide-react";
import { businessId, site, telephoneHref } from "@/lib/site";

export interface SeoLandingFAQ {
  q: string;
  a: string;
}

export interface SeoLandingProps {
  slug: string; // e.g. "estate-cleanout-wisconsin"
  h1: string;
  metaTitle: string;
  metaDescription: string;
  heroImage: string; // public path
  heroIntro: string; // 1-2 sentences, emotional/intent-matched
  serviceName: string; // for Service schema
  bullets: string[]; // what's included
  whyUs: string[]; // differentiators specific to intent
  processSteps: { title: string; detail: string }[];
  faqs: SeoLandingFAQ[];
  ctaHeadline: string;
}

const SeoLandingPage = ({
  slug,
  h1,
  metaTitle,
  metaDescription,
  heroImage,
  heroIntro,
  serviceName,
  bullets,
  whyUs,
  processSteps,
  faqs,
  ctaHeadline,
}: SeoLandingProps) => {
  const url = `${site.siteUrl}/services/${slug}`;
  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: serviceName,
          serviceType: serviceName,
          provider: {
            "@id": businessId,
          },
          areaServed: { "@type": "State", name: "Wisconsin" },
          description: metaDescription,
          url,
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${site.siteUrl}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${site.siteUrl}/services` },
            { "@type": "ListItem", position: 3, name: serviceName, item: url },
          ],
        })}
      </script>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}
      </script>

      {/* Hero */}
      <section
        className="relative py-16 md:py-24 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url('${heroImage}')` }}
      >
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
        <div className="container relative z-10 text-center max-w-3xl">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-white leading-tight mb-5">
            {h1}
          </h1>
          <p className="text-white/90 text-lg mb-8">{heroIntro}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="secondary" asChild className="font-semibold">
              <Link to="/book">Get an Estimate in 15 Minutes</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="font-semibold bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
            >
              <a href={telephoneHref}>
                <Phone className="w-5 h-5 mr-2" />
                {site.phone}
              </a>
            </Button>
          </div>
          <p className="text-white/80 text-sm mt-5 flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" />
              Serving Northeast Wisconsin since {site.foundingYear}
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="py-14 md:py-20">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-8 text-center">
            What's Included
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {bullets.map((b) => (
              <div key={b} className="flex items-start gap-3 p-4 rounded-lg bg-card border">
                <CheckCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground leading-relaxed">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 md:py-20 bg-muted">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-8 text-center">
            Why Wisconsin Chooses Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyUs.map((w) => (
              <Card key={w}>
                <CardContent className="p-5 flex gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-1" />
                  <p className="text-sm text-foreground leading-relaxed">{w}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 md:py-20">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-10 text-center">
            How It Works
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={step.title} className="flex gap-5 items-start">
                <div className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground font-heading font-bold flex items-center justify-center">
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-muted">
        <div className="container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group border rounded-lg bg-card">
                <summary className="cursor-pointer p-5 font-heading font-semibold text-foreground flex justify-between items-center">
                  {f.q}
                  <span className="ml-2 text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative py-14 md:py-20 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url('${heroImage}')` }}
      >
        <div className="absolute inset-0 bg-black/65" aria-hidden="true" />
        <div className="container relative z-10 text-center max-w-2xl">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">{ctaHeadline}</h2>
          <p className="text-white/85 mb-7">
             Upload project photos and request a clear, no-obligation demolition estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" variant="secondary" asChild className="font-semibold">
              <Link to="/book">
                 Request My Estimate <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="font-semibold bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
            >
              <a href={telephoneHref}>
                <Phone className="w-5 h-5 mr-2" />
                Call {site.phone}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default SeoLandingPage;
